/**
 * Typed entry point for report tool usage analytics — the report-side twin of
 * `drawing-analytics.ts`. One family (REPORT_FEATURE_USED), the specific
 * action in `context.feature`; the union keeps the vocabulary in one place and
 * makes a typo a compile error, since the server deliberately does not
 * validate it. The back-office mirrors these keys in its label Records.
 *
 * Report generation itself is NOT tracked here: the backend already emits the
 * legacy REPORT_GENERATED (now carrying the display settings used), and the
 * catalogue keeps its legacy REPORT_USE_CATALOGUE alongside `finding_created
 * {via: "catalogue"}` during the transition.
 */
import { EventType } from "@/types/event-logs.types";
import type { EventContext } from "@/types/event-logs.types";
import { enqueueEvent } from "./event-queue";

export type ReportStep =
  | "metadata"
  | "photos"
  | "findings"
  | "overviews"
  | "conclusions";

export type ReportFeature =
  // Navigation
  | "step_viewed"
  // Metadata
  | "metadata_field_edited"
  // Photos step
  | "photos_imported"
  | "photo_edited"
  | "photo_moved"
  | "photos_sorted"
  | "photo_size_changed"
  | "view_mode_toggled"
  | "section_created"
  | "section_deleted"
  | "sections_template_saved"
  | "sections_template_inserted"
  // Findings step
  | "finding_created"
  | "finding_edited"
  | "finding_moved"
  | "finding_deleted"
  | "source_observation_viewed"
  // Overviews step
  | "overview_added"
  | "overview_configured"
  // AI generation (started — completion is asynchronous server-side)
  | "findings_generated_ai"
  | "modular_generation_started"
  // Conclusions & free text
  | "conclusions_edited"
  | "text_content_edited";

export function trackReportFeature(
  feature: ReportFeature,
  details?: EventContext,
): void {
  enqueueEvent(EventType.REPORT_FEATURE_USED, { feature, ...details });
}
